import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { LoadingStatusWrapper, PopOutChart, InfoMessage, GeneralButton } from 'sharedComponents';
import { useTranslation } from 'react-i18next';
import use2DChartConfig, { getTableHighestPeaks } from './use2DChartConfig';
import { chartModules, PiezoSensor, AssetTypes } from './constants';
import '../style.scss';
import { getSpectrumRangeOptions } from '../spectrumRangeOptions';
import {
    ChartViewToggleButton,
    PopOutButton,
    ResetZoomButton,
    withLeftButtonGroup,
    withPopOutRow,
    withRightButtonGroup,
    XAxisToggleButton
} from 'sharedComponents/Chart/headers';
import { map, some } from 'lodash';
import FFT2DHistoricalTimestampDialog from './FFT2DHistoricalTimestampDialog';
import { chartHeightConstants } from '../constants';
import { apiCallStatus } from 'helpers/constants';
import useChartResize from 'sharedHooks/useChartResize';
import BearingFaultHarmonics from './BearingFaultHarmonics';
import HarmonicsIndicators from './HarmonicsIndicators';
import SpeedHarmonicsTable from './SpeedHarmonicsTable';
import BearingFaultHarmonicsTable from './BearingFaultHarmonicsTable';


const VibrationFFT2DComponent = (props) => {

    const { t: translate } = useTranslation();
    const { model, actions, selectedTimestamps } = props;
    const [isZoomed, setIsZoomed] = useState(false);
    const spectrumRangeOptions = getSpectrumRangeOptions(translate, model.speed, model.sensorTypeID, model.fft2DData);
    const containerRef = useRef(null);
    const chartConfig = use2DChartConfig(
        model.fft2DData,
        model.selected2DLegends,
        model.selectedSpectrumRange,
        actions.setSelected2DLegends,
        containerRef,
        model.bearingFaultSwitch,
        model.bearingFrequencies,
        model.bearingFaultType,
        model.speedHarmonicsSwitch
    );
    const [showHistoricalDialog, setShowHistoricalDialog] = useState(false);
    const [chartInstance, setChartInstance] = useState(null);
    const chartInstanceRef = useRef(null);
    const chartRef = useRef();
    const fftData = false;
    const bearingFaultFrequencies = model?.fft2DData?.[0]?.bearingFaultFrequencies;
    const harmonics = model?.fft2DData?.[0]?.harmonics;
    const harmonicsHighestPeaks = model?.fft2DData?.[0]?.harmonicsHighestPeaks;
    const frequencyUnitSymbol = model?.fft2DData?.[0]?.frequencyUnitSymbol;
    const magnitudeUnitSymbol = model?.fft2DData?.[0]?.magnitudeUnitSymbol;
    const precision = model?.fft2DData?.[0]?.precision;

    const hasBearingFaultFrequencies = model?.fft2DData?.[0]?.hasBearingFaultFrequencies;
    const hasHarmonics = model?.fft2DData?.[0]?.hasHarmonics;
    const hasSpeed = model?.fft2DData?.[0]?.hasSpeed;
    const isGeneralMachinery = model?.assetType === AssetTypes.GENERAL_MACHINERY && (model?.speed === null || model?.speed === undefined);
    const filteredHarmonicsHighestPeaks = getTableHighestPeaks(
        harmonicsHighestPeaks,
        model?.fft2DData?.[0],
        model.selectedSpectrumRange,
        model.selected2DLegends
    );

    const getValue = () => {
        return spectrumRangeOptions.find(option => option.id === model.selectedSpectrumRange);
    };
    const setZoomBySpectrumRange = (mainChartInst) => (xAxisExtreme) => {
        mainChartInst?.xAxis[0]?.setExtremes(0, xAxisExtreme);
        const xAxisZeroExtremes = mainChartInst?.xAxis[0].getExtremes();
        mainChartInst?.xAxis[1]?.setExtremes(0, xAxisZeroExtremes.max * 60);
        mainChartInst?.yAxis[0]?.setExtremes();
    };
    const zoomOut = (mainChartInst) => {
        mainChartInst.zoomOut();
        const selectionValue = getValue();
        setZoomBySpectrumRange(mainChartInst)(selectionValue ? selectionValue.xAxisZoomValue : spectrumRangeOptions[1].xAxisZoomValue);
    };

    const updateChartHeight = () => {
        if (chartInstanceRef?.current && Object.keys(chartInstanceRef?.current)?.length) {
            const chartContainer = containerRef?.current?.querySelector('#op-fft-2d-chart-v2');
            const height = chartContainer?.clientHeight || chartHeightConstants.default;
            const finalHeight = Math.min(Math.max(height, chartHeightConstants.min), chartHeightConstants.max);
            chartInstanceRef.current.update({
                chart: {
                    height: finalHeight
                }
            }, true, true);
        }
    };

    useEffect(() => {
        const parent = containerRef.current?.parentElement;
        if (!parent) { return; }

        let lastHeight = null;
        let timeoutId = null;

        const observer = new ResizeObserver((entries) => {
            const containerHeight = entries[0]?.contentRect?.height;
            if (containerHeight === lastHeight) { return; }
            lastHeight = containerHeight;
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => updateChartHeight(), 0);
        });

        observer.observe(parent);

        return () => {
            observer.disconnect();
            clearTimeout(timeoutId);
        };
    }, [model?.speedHarmonicsSwitch, model?.bearingFaultSwitch]);

    useEffect(() => {
        if (model?.fft2DDataLoadingStatus === apiCallStatus.SUCCESS && model?.fft2DData) {
            const isPiezoAvailable = some(model?.fft2DData, (fft) => fft?.dataValues?.some(data => data.sensorAxisName === PiezoSensor.axisName));
            if (!isPiezoAvailable && model?.selectedSpectrumRange === PiezoSensor.id) {
                actions.setSelectedSpectrumRange(spectrumRangeOptions[1]?.id);
            }
            // Reset switches when FFT date/data or speed changes
            actions.setBearingFaultSwitch(false);
            actions.setSpeedHarmonicsSwitch(false);
        }
    }, [model?.fft2DData, model?.speed]);

    useEffect(() => {
        chartInstanceRef.current = chartInstance;
        if (chartInstance) {
            setTimeout(() => {
                if (chartInstanceRef?.current && Object.keys(chartInstanceRef?.current)?.length) {
                    chartInstanceRef.current.reflow();
                    updateChartHeight();
                }
            }, 300);
        }
    }, [chartInstance]);

    useChartResize(containerRef, chartRef);

    const historicalTimeStamp = (props) => {
        const { mainChartInst, setIsZoomed } = props;
        return mainChartInst ? <div key='fft-historical' className='historical-timestamp'>
            <GeneralButton
                type='normal'
                text={translate('ABB.Powertrain.Frontend.vibrationFFTSelectTimestamp')}
                onClick={() => setShowHistoricalDialog(true)}
            />
            <div className='fft-options'>
                {
                    map(spectrumRangeOptions, option =>
                        <>
                            {option.id === PiezoSensor.id && <div className='vertical-line' />}
                            <GeneralButton
                                key={option.id}
                                type='discreet'
                                className={`fft-option-btn ${option.id === model.selectedSpectrumRange ? 'selected' : ''}`}
                                text={option.title}
                                onClick={() => {
                                    setZoomBySpectrumRange(mainChartInst)(option.xAxisZoomValue);
                                    actions.setSelectedSpectrumRange(option.id);
                                    setIsZoomed(false);
                                }}
                            />
                        </>
                    )
                }
            </div>

        </div> : null;
    };

    const leftButtonGroupOptions = (props) => {
        return <div className='left-header-fft-options'>
            {historicalTimeStamp(props)}
            {!isGeneralMachinery &&
                <div className='frequency-harmonics-options'>
                    <HarmonicsIndicators {...props} />
                    <BearingFaultHarmonics {...props} />
                </div>
            }
        </div>;
    };

    const leftButtonGroupPopOutOptions = (props) => {
        return <div className='left-header-fft-options'>
            {!isGeneralMachinery &&
                <div className='frequency-harmonics-options'>
                    <HarmonicsIndicators {...props} />
                    <BearingFaultHarmonics {...props} />
                </div>
            }
        </div>;
    };

    const HarmonicsTable = () => {
        const showSpeedHarmonics = model?.speedHarmonicsSwitch && hasHarmonics;
        const showBearingFaults = model?.bearingFaultSwitch && hasBearingFaultFrequencies;
        return (
            <div className='harmonics-details-wrapper'>
                {showSpeedHarmonics &&
                    <SpeedHarmonicsTable
                        harmonics={harmonics}
                        harmonicsHighestPeaks={filteredHarmonicsHighestPeaks}
                        frequencyUnitSymbol={frequencyUnitSymbol}
                        precision={precision}
                    />
                }
                {showBearingFaults &&
                    <BearingFaultHarmonicsTable
                        bearingFaultFrequencies={bearingFaultFrequencies}
                        frequencyUnitSymbol={frequencyUnitSymbol}
                        bearingFaultType={model?.bearingFaultType}
                    />
                }
            </div>
        );
    };

    const MainChartHeader = withPopOutRow([
        withLeftButtonGroup([leftButtonGroupOptions]),
        withRightButtonGroup([ResetZoomButton, XAxisToggleButton, ChartViewToggleButton, PopOutButton])
    ]);

    const PopOutChartHeader = withPopOutRow([
        withLeftButtonGroup([leftButtonGroupPopOutOptions]),
        withRightButtonGroup([ResetZoomButton, XAxisToggleButton, PopOutButton])
    ]);

    return (
        <div
            ref={containerRef}
            style={{ height: '100%' }}
            className='vibration-fft-2d-component'
        >
            <LoadingStatusWrapper
                useCustomAnchor
                transparent
                indicatorConfig={{ placement: 'sticky' }}
                loadingStatus={[model?.fft2DDataLoadingStatus]} >
                <PopOutChart
                    hasKpi={model?.hasRawData}
                    data={fftData}
                    renderNoKpi={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.noDataLabelAvailable'
                        text='ABB.Powertrain.Frontend.collectRawDataLabel' />}
                    isZoomed={isZoomed}
                    setIsZoomed={setIsZoomed}
                    loadingStatus={model?.fft2DDataLoadingStatus}
                    options={chartConfig}
                    modules={chartModules}
                    container='op-fft-2d-chart-v2'
                    labelLastSync={translate('ABB.Powertrain.Frontend.labelLastRawDataSync')}
                    zoomOut={zoomOut}
                    selectedChartView={model.selectedChartView}
                    setSelectedChartView={actions.setSelectedChartView}
                    mainChartHeader={MainChartHeader}
                    popOutChartHeader={PopOutChartHeader}
                    popOutFooter={<HarmonicsTable />}
                    onChartReady={setChartInstance}
                    chartInstance={chartInstance}
                    chartRef={chartRef}
                    isVibrationFFT2D={true}
                    setZoomBySpectrumRange={setZoomBySpectrumRange}
                    spectrumRangeOptions={spectrumRangeOptions}
                    selectedSpectrumRange={model.selectedSpectrumRange}
                    selectedXAxisUnit={model.selectedXAxisUnit}
                    setSelectedXAxisUnit={actions.setSelectedXAxisUnit}
                    bearingFaultType={model?.bearingFaultType}
                    bearingFrequencies={model?.bearingFrequencies}
                    bearingFaultSwitch={model?.bearingFaultSwitch}
                    setBearingFaultType={actions.setBearingFaultType}
                    setBearingFaultFrequencies={actions.setBearingFaultFrequencies}
                    setBearingFaultSwitch={actions.setBearingFaultSwitch}
                    bearingFaultFrequencies={bearingFaultFrequencies}
                    harmonics={harmonics}
                    magnitudeUnitSymbol={magnitudeUnitSymbol}
                    frequencyUnitSymbol={frequencyUnitSymbol}
                    hasBearingFaultFrequencies={hasBearingFaultFrequencies}
                    speedHarmonicsSwitch={model?.speedHarmonicsSwitch}
                    setSpeedHarmonicsSwitch={actions.setSpeedHarmonicsSwitch}
                    hasHarmonics={hasHarmonics}
                    hasSpeed={hasSpeed}
                />
                {showHistoricalDialog &&
                    <FFT2DHistoricalTimestampDialog
                        showHistoricalDialog={showHistoricalDialog}
                        setShowHistoricalDialog={setShowHistoricalDialog}
                        setSelectedDates={actions.setSelectedDates}
                        dateSelection={model.dateSelection}
                        getFFTTimestampList={actions.getFFTTimestampList}
                        fftTimestampList={model.fftTimestampList}
                        getFFTDataByTimestamp={actions.getFFTDataByTimestamp}
                        fftDataLoadingStatus={model?.fft2DDataLoadingStatus}
                        selectedTimestamp={selectedTimestamps?.[0]}
                        setSelectedTimestamp={actions.setSelected2DTimestamp}
                        selectedOrganization={model.selectedOrganization}
                        selectedAsset={model.selectedAsset}
                        trendKPIsList={model?.trendKPIsList}
                        maxPrecision={model?.fft2DData?.[0]?.precision}
                    />
                }
            </LoadingStatusWrapper>
            <HarmonicsTable />
        </div>
    );
};

VibrationFFT2DComponent.propTypes = {
    actions: PropTypes.shape({
        getDownloadRawData: PropTypes.func,
        getFFTTimestampList: PropTypes.func,
        getFFTDataByTimestamp: PropTypes.func,
        setSelectedDates: PropTypes.func,
        setSelected2DTimestamp: PropTypes.func,
        setSelected2DLegends: PropTypes.func,
        setSelectedSpectrumRange: PropTypes.func,
        setSelectedChartView: PropTypes.func,
        setSelectedXAxisUnit: PropTypes.func,
        setBearingFaultType: PropTypes.func,
        setBearingFaultFrequencies: PropTypes.func,
        setBearingFaultSwitch: PropTypes.func,
        setSpeedHarmonicsSwitch: PropTypes.func
    }).isRequired,
    model: PropTypes.shape({
        fft2DData: PropTypes.arrayOf(PropTypes.shape({
            dataValues: PropTypes.array,
            precision: PropTypes.number,
            bearingFaultFrequencies: PropTypes.array,
            frequencyUnitSymbol: PropTypes.string,
            magnitudeUnitSymbol: PropTypes.string,
            hasBearingFaultFrequencies: PropTypes.bool,
            hasHarmonics: PropTypes.bool,
            hasSpeed: PropTypes.bool,
            harmonics: PropTypes.array,
            harmonicsHighestPeaks: PropTypes.arrayOf(PropTypes.shape({
                frequencyValue: PropTypes.number,
                magnitude: PropTypes.number,
                order: PropTypes.number
            }))
        })),
        fft2DDataLoadingStatus: PropTypes.string,
        getFFTDataLoadingStatus: PropTypes.string,
        measurementExportLoadingStatus: PropTypes.string,
        trendDataLoadingStatus: PropTypes.string,
        dateSelection: PropTypes.shape({
            from: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            to: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        }),
        fftTimestampList: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        speed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        sensorTypeID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        hasRawData: PropTypes.bool,
        selected2DLegends: PropTypes.object,
        selectedSpectrumRange: PropTypes.string,
        selectedChartView: PropTypes.string,
        selectedXAxisUnit: PropTypes.string,
        bearingFaultType: PropTypes.string,
        assetType: PropTypes.string,
        bearingFrequencies: PropTypes.object,
        bearingFaultSwitch: PropTypes.bool,
        speedHarmonicsSwitch: PropTypes.bool,
        selectedOrganization: PropTypes.object,
        selectedAsset: PropTypes.object,
        trendKPIsList: PropTypes.array
    }).isRequired,
    selectedTimestamps: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object
    ])),
    hasKpi: PropTypes.bool,
    hasHarmonics: PropTypes.bool,
    speedHarmonicsSwitch: PropTypes.bool
};

export default VibrationFFT2DComponent;
